<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingSeriesPoint, IBankingSeriesResponse } from './types'
import { useBankingNumber } from './useBankingNumber'

type RatesProduct = 'both' | 'credits' | 'deposits'
type RatesCurrency = 'kgs' | 'fx'
type RatesRange = '1y' | '2y' | '5y' | 'all'
type RatesLine = 'credits' | 'deposits'

const props = defineProps<{
  asOf: string | null
}>()

const PRODUCTS: RatesProduct[] = ['both', 'credits', 'deposits']
const CURRENCIES: RatesCurrency[] = ['kgs', 'fx']
const RANGES: RatesRange[] = ['1y', '2y', '5y', 'all']
const RANGE_YEARS: Record<Exclude<RatesRange, 'all'>, number> = { '1y': 1, '2y': 2, '5y': 5 }

const SERIES_CODES: Record<RatesCurrency, Record<RatesLine, string>> = {
  kgs: {
    credits: 'credits.stock.kgs.total.all.rate',
    deposits: 'deposits.stock.all.kgs.all.rate'
  },
  fx: {
    credits: 'credits.stock.fx.total.all.rate',
    deposits: 'deposits.stock.all.fx.all.rate'
  }
}

const LINE_COLORS: Record<RatesLine, string> = { credits: '#dc2626', deposits: '#2563eb' }

const { t } = useI18n()
const { formatPercent, formatDate } = useBankingNumber()

const product = ref<RatesProduct>('both')
const currency = ref<RatesCurrency>('kgs')
const range = ref<RatesRange>('2y')

const requestedLines = computed<RatesLine[]>(() =>
  product.value === 'both' ? ['credits', 'deposits'] : [product.value]
)

/** First day of the month `years` years before the last available point, so the range does not depend on today. */
const fromDate = computed<string | undefined>(() => {
  if (range.value === 'all' || !props.asOf) return undefined

  const [year, month] = props.asOf.split('-').map(Number)

  if (!year || !month) return undefined

  return `${year - RANGE_YEARS[range.value]}-${String(month).padStart(2, '0')}-01`
})

const requestParams = computed(() => ({
  'codes[]': requestedLines.value.map((line) => SERIES_CODES[currency.value][line]),
  ...(fromDate.value ? { from: fromDate.value } : {})
}))

const { data: response, status } = useApi<IBankingSeriesResponse>('/banking-statistics/series', {
  params: requestParams,
  key: 'banking-statistics-rates',
  watch: [requestParams],
  lazy: true,
  server: false
})

interface IDisplayedLine {
  line: RatesLine
  points: IBankingSeriesPoint[]
}

const CODE_TO_LINE = new Map<string, RatesLine>(
  Object.values(SERIES_CODES).flatMap((byLine) =>
    (Object.keys(byLine) as RatesLine[]).map((line): [string, RatesLine] => [byLine[line], line])
  )
)

// The chart is built from a snapshot taken when a response arrives, not from the current switches.
// While a new request is in flight the previous chart therefore stays on screen unchanged.
const displayed = shallowRef<IDisplayedLine[]>([])

watch(
  response,
  (value) => {
    const map = value?.data

    if (!map || Array.isArray(map)) return

    const snapshot = Object.entries(map).flatMap(([code, series]): IDisplayedLine[] => {
      const line = CODE_TO_LINE.get(code)

      return line ? [{ line, points: series.points }] : []
    })

    // A deposits/credits order that does not depend on the order of the response keys.
    displayed.value = snapshot.sort((a, b) => (a.line === b.line ? 0 : a.line === 'credits' ? -1 : 1))
  },
  { immediate: true }
)

const lines = computed<RatesLine[]>(() => displayed.value.map((item) => item.line))

const lineSeries = (line: RatesLine): { points: IBankingSeriesPoint[] } | undefined =>
  displayed.value.find((item) => item.line === line)

const periods = computed<string[]>(() => {
  const all = new Set<string>()

  for (const item of displayed.value) {
    item.points.forEach((point) => all.add(point.period))
  }

  return [...all].sort()
})

const preliminaryCount = computed(() => {
  const preliminary = new Set<string>()

  for (const item of displayed.value) {
    item.points.forEach((point) => {
      if (point.is_preliminary && point.value !== null) preliminary.add(point.period)
    })
  }

  return preliminary.size
})

const hasData = computed(() => periods.value.length > 0)
// Only a refetch over a drawn chart dims it: on hydration the server status ('idle') and the
// client status ('pending') differ, and the class must match the server HTML.
const isLoading = computed(() => status.value === 'pending' && hasData.value)
const hasFailed = computed(
  () => !hasData.value && (status.value === 'error' || status.value === 'success')
)

const formatMonth = (period: string): string => {
  const [year, month] = period.split('-')

  return year && month ? `${month}.${year}` : period
}

const lineLabel = (line: RatesLine): string => t(`banking_statistics.rates.line_${line}`)

const chartOption = computed<EChartsOption>(() => {
  const axis = periods.value

  const lineData = (line: RatesLine) => {
    const byPeriod = new Map(lineSeries(line)?.points.map((point) => [point.period, point]))

    return axis.map((period) => byPeriod.get(period) ?? null)
  }

  const series = lines.value.flatMap((line) => {
    const color = LINE_COLORS[line]
    const points = lineData(line)

    const confirmed = points.map((point) => (point?.is_preliminary ? null : (point?.value ?? null)))

    // A preliminary month is drawn as a separate translucent dashed segment that also reaches
    // the neighbouring confirmed point, so it joins the solid line without a gap.
    const preliminary = points.map((point, index) => {
      const isBridge =
        point?.is_preliminary === true ||
        points[index - 1]?.is_preliminary === true ||
        points[index + 1]?.is_preliminary === true

      return isBridge ? (point?.value ?? null) : null
    })

    const hasPreliminary = preliminary.some((value) => value !== null)

    const common = {
      type: 'line' as const,
      smooth: false,
      showSymbol: false,
      connectNulls: false,
      color
    }

    const endLabel = {
      show: true,
      formatter: (params: { value?: unknown }) =>
        typeof params.value === 'number' ? formatPercent(params.value) : '',
      color,
      fontWeight: 600,
      fontSize: 12
    }

    return [
      {
        ...common,
        name: line,
        data: confirmed,
        lineStyle: { width: 2.5, color },
        ...(hasPreliminary ? {} : { endLabel })
      },
      ...(hasPreliminary
        ? [
            {
              ...common,
              name: `${line}-preliminary`,
              data: preliminary,
              lineStyle: { width: 2.5, color, type: 'dashed' as const, opacity: 0.45 },
              showSymbol: true,
              symbolSize: 8,
              itemStyle: { color, opacity: 0.5 },
              endLabel
            }
          ]
        : [])
    ]
  })

  return {
    grid: { left: 8, right: 72, top: 24, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: axis.map(formatMonth),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { fontSize: 11, color: '#6b7280' }
    },
    yAxis: {
      type: 'value',
      scale: true,
      axisLabel: { formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#f3f4f6' } }
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params: unknown) => {
        const first = (params as { dataIndex?: number }[])[0]
        const index = first?.dataIndex

        if (index === undefined) return ''

        const period = axis[index]

        if (!period) return ''

        let isPreliminary = false

        const rows = lines.value
          .map((line) => {
            const point = lineSeries(line)?.points.find((item) => item.period === period)

            if (!point || point.value === null) return ''
            if (point.is_preliminary) isPreliminary = true

            return `<span style="color:${LINE_COLORS[line]}">●</span> ${lineLabel(line)}: <strong>${formatPercent(point.value)}</strong>`
          })
          .filter(Boolean)
          .join('<br/>')

        const mark = isPreliminary
          ? `<br/><span>${t('banking_statistics.preliminary')}</span>`
          : ''

        return `<strong>${formatDate(period)}</strong><br/>${rows}${mark}`
      }
    },
    series
  }
})
</script>

<template>
  <section id="rates" class="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
    <div class="mb-4 flex flex-col gap-1">
      <h2 class="text-lg font-semibold">{{ t('banking_statistics.rates.title') }}</h2>
      <p class="text-muted-foreground text-sm">
        {{ t('banking_statistics.rates.subtitle') }}
      </p>
    </div>

    <div class="mb-4 flex flex-wrap gap-x-6 gap-y-2">
      <div data-testid="rates-product" class="flex flex-wrap gap-2">
        <Button
          v-for="item in PRODUCTS"
          :key="item"
          :variant="product === item ? 'blue' : 'outline-gray'"
          size="sm"
          :aria-pressed="product === item"
          @click="product = item"
        >
          {{ t(`banking_statistics.rates.product_${item}`) }}
        </Button>
      </div>

      <div data-testid="rates-currency" class="flex flex-wrap gap-2">
        <Button
          v-for="item in CURRENCIES"
          :key="item"
          :variant="currency === item ? 'blue' : 'outline-gray'"
          size="sm"
          :aria-pressed="currency === item"
          @click="currency = item"
        >
          {{ t(`banking_statistics.rates.currency_${item}`) }}
        </Button>
      </div>

      <div data-testid="rates-range" class="flex flex-wrap gap-2">
        <Button
          v-for="item in RANGES"
          :key="item"
          :variant="range === item ? 'blue' : 'outline-gray'"
          size="sm"
          :aria-pressed="range === item"
          @click="range = item"
        >
          {{ t(`banking_statistics.rates.range_${item}`) }}
        </Button>
      </div>
    </div>

    <div
      data-testid="rates-chart"
      :data-preliminary-count="preliminaryCount"
      :class="['relative', isLoading && 'opacity-70 transition-opacity']"
    >
      <LazyVChart
        v-if="hasData"
        :option="chartOption"
        autoresize
        class="h-72 w-full sm:h-80"
        :update-options="{ notMerge: true }"
      />
      <div
        v-else-if="hasFailed"
        data-testid="rates-empty"
        class="text-muted-foreground flex h-72 w-full items-center justify-center rounded-lg bg-gray-50 text-sm sm:h-80"
      >
        {{ t('banking_statistics.rates.unavailable') }}
      </div>
      <div v-else class="h-72 w-full animate-pulse rounded-lg bg-gray-100 sm:h-80" />
    </div>

    <div class="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
      <span v-if="asOf">{{ t('banking_statistics.source_as_of', { date: formatDate(asOf) }) }}</span>
      <span v-else>{{ t('banking_statistics.source') }}</span>
      <span v-if="preliminaryCount">{{ t('banking_statistics.rates.preliminary_note') }}</span>
    </div>
  </section>
</template>
